import { createSlice, createAction } from '@reduxjs/toolkit';

// Saga action creators
export const fetchCampaignsRequest = createAction('FETCH_CAMPAIGNS');
export const fetchCampaignAnalyticsRequest = createAction('FETCH_CAMPAIGN_ANALYTICS');
export const createCampaignScheduleRequest = createAction('CREATE_CAMPAIGN_SCHEDULE');
export const updateCampaignScheduleRequest = createAction('UPDATE_CAMPAIGN_SCHEDULE');
export const cancelCampaignRequest = createAction('CANCEL_CAMPAIGN_SCHEDULE');

const initialState = {
  list: [],
  stats: {
    totalCampaigns: 0,
    messagesSent: '0',
    avgClickRate: '0%',
    totalSpent: '₹0'
  },
  filter: 'all', // all | completed | scheduled | cancelled
  searchQuery: '',
  loading: false,
  creating: false,
  updating: false,
  cancelling: false,
  error: null,
  createError: null,
  createSuccess: false,
  lastCreatedCampaignId: null,
  analytics: null,
  analyticsLoading: false,
  analyticsError: null,
  lastAnalyticsCampaignId: null,
};

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    // Fetch campaigns
    fetchCampaignsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCampaignsSuccess: (state, action) => {
      state.list = action.payload.list || [];
      if (action.payload.stats) {
        state.stats = action.payload.stats;
      }
      state.loading = false;
    },
    fetchCampaignsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Create campaign schedule
    createCampaignScheduleStart: (state) => {
      state.creating = true;
      state.createError = null;
      state.createSuccess = false;
      state.lastCreatedCampaignId = null;
    },
    createCampaignScheduleSuccess: (state, action) => {
      state.creating = false;
      state.createSuccess = true;
      state.lastCreatedCampaignId = action.payload?.campaignId || null;
    },
    createCampaignScheduleFailure: (state, action) => {
      state.creating = false;
      state.createError = action.payload;
    },
    resetCreateState: (state) => {
      state.creating = false;
      state.createError = null;
      state.createSuccess = false;
      state.lastCreatedCampaignId = null;
    },
    // Update campaign schedule
    updateCampaignScheduleStart: (state) => {
      state.updating = true;
    },
    updateCampaignScheduleSuccess: (state, action) => {
      state.updating = false;
      const { campaignId, scheduledAt } = action.payload || {};
      const campaign = state.list.find(
        (c) => c.campaign_id === campaignId || c.campaignId === campaignId || c.id === campaignId
      );
      if (campaign) {
        campaign.scheduled_at = scheduledAt;
      }
    },
    updateCampaignScheduleFailure: (state) => {
      state.updating = false;
    },
    // Cancel campaign
    cancelCampaignStart: (state) => {
      state.cancelling = true;
    },
    cancelCampaignSuccess: (state, action) => {
      state.cancelling = false;
      const { campaignId } = action.payload || {};
      const campaign = state.list.find(
        (c) => c.campaign_id === campaignId || c.campaignId === campaignId || c.id === campaignId
      );
      if (campaign) {
        campaign.status = 'CANCELLED';
      }
    },
    cancelCampaignFailure: (state) => {
      state.cancelling = false;
    },
    fetchCampaignAnalyticsStart: (state) => {
      state.analyticsLoading = true;
      state.analyticsError = null;
    },
    fetchCampaignAnalyticsSuccess: (state, action) => {
      state.analyticsLoading = false;
      state.analytics = action.payload;
      state.lastAnalyticsCampaignId = action.payload?.campaignId || null;
      state.analyticsError = null;
    },
    fetchCampaignAnalyticsFailure: (state, action) => {
      state.analyticsLoading = false;
      state.analyticsError = action.payload;
    },
    clearCampaignAnalytics: (state) => {
      state.analytics = null;
      state.analyticsLoading = false;
      state.analyticsError = null;
      state.lastAnalyticsCampaignId = null;
    },
  }
});

export const {
  setFilter, setSearchQuery,
  fetchCampaignsStart, fetchCampaignsSuccess, fetchCampaignsFailure,
  createCampaignScheduleStart, createCampaignScheduleSuccess, createCampaignScheduleFailure, resetCreateState,
  updateCampaignScheduleStart, updateCampaignScheduleSuccess, updateCampaignScheduleFailure,
  cancelCampaignStart, cancelCampaignSuccess, cancelCampaignFailure,
  fetchCampaignAnalyticsStart, fetchCampaignAnalyticsSuccess, fetchCampaignAnalyticsFailure, clearCampaignAnalytics,
} = campaignsSlice.actions;

export default campaignsSlice.reducer;